import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ArrowLeft, CalendarDays, Check, ChevronDown, ChevronRight, LineChart as LineIcon, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { formatCZK, formatCZKCompact, formatHours, plur } from "@/lib/format";
import { BottomSheet, TabSwitcher, useIsMobile } from "@/components/shared/PagePrimitives";
import { LineChart, ColumnChart, Legend, TipRow, monthName, monthShort, monthGen, cap } from "./charts";
import "./ecoOverview.css";
import { autoPill } from "@/components/shared/Pill";

// Přehled Ekonomiky (227): tři grafy — Příjmy a výdaje (peníze dopředu, 289), Náklady
// (stín všech nákladů + vybraný druh) a Zakázky (rozpočet × náklady). Každý má vlastní detail.

const C = { in: "var(--ov-in)", out: "var(--ov-out)", red: "var(--ov-red)", budget: "var(--ov-budget)", muted: "hsl(var(--muted-foreground))" };
const CAT_COLORS = ["var(--ov-c0)", "var(--ov-c1)", "var(--ov-c2)", "var(--ov-c3)", "var(--ov-c4)"];
const money = (v) => formatCZKCompact(Math.round(v || 0));
const signed = (v) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${formatCZKCompact(Math.abs(Math.round(v || 0)))}`;
const pct = (v) => `${Math.round(v)} %`;
const sum = (a) => a.reduce((t, x) => t + (x || 0), 0);

function ym(d) { return d.toISOString().slice(0, 7); }
function addMonths(m, n) { const d = new Date(`${m}-15T12:00:00Z`); d.setUTCMonth(d.getUTCMonth() + n); return ym(d); }
function thisMonth() { return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Prague", year: "numeric", month: "2-digit" }).format(new Date()).slice(0, 7); }

function usePeriod() {
  const [params, setParams] = useSearchParams();
  const cur = thisMonth();
  const od = params.get("od") || `${cur.slice(0, 4)}-01`;
  const doM = params.get("do") || cur;
  const set = (o, d) => setParams((p) => { const n = new URLSearchParams(p); n.set("od", o); n.set("do", d); return n; }, { replace: true });
  const presets = [
    { key: "rok", label: "Letos", long: "Celý rok", od: `${cur.slice(0, 4)}-01`, do: cur },
    { key: "m6", label: "6 měsíců", long: "Posledních 6 měsíců", od: addMonths(cur, -5), do: cur },
    { key: "m3", label: "3 měsíce", long: "Poslední 3 měsíce", od: addMonths(cur, -2), do: cur },
  ];
  const active = presets.find((p) => p.od === od && p.do === doM);
  const options = [];
  for (let m = addMonths(cur, -23); m <= cur; m = addMonths(m, 1)) options.push(m);
  const label = active ? (active.key === "rok" ? "letos" : `za ${active.label}`) : `${monthName(od)} – ${monthName(doM)}`;
  return { od, do: doM, set, presets, active, options, label, cur };
}

function useView() {
  const [params, setParams] = useSearchParams();
  const view = params.get("graf") || "home";
  const tab = params.get("karta") || "";
  const go = (v, t = "") => setParams((p) => {
    const n = new URLSearchParams(p);
    if (v === "home") { n.delete("graf"); n.delete("karta"); } else { n.set("graf", v); if (t) n.set("karta", t); else n.delete("karta"); }
    return n;
  });
  return { view, tab, go };
}

// ---------- ovládání období ----------
function PeriodDesktop({ p }) {
  const sel = "h-9 rounded-full bg-secondary px-3 text-sm font-semibold border-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  return (
    <div className="hidden md:flex items-center gap-2 flex-wrap" data-testid="ov-period">
      <div className="inline-flex rounded-full border border-border bg-card p-1" role="group" aria-label="Období">
        {p.presets.map((x) => (
          <button key={x.key} type="button" onClick={() => p.set(x.od, x.do)} aria-pressed={p.active?.key === x.key}
            className={`h-8 px-3.5 rounded-full text-sm font-bold whitespace-nowrap ${p.active?.key === x.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {x.key === "rok" ? "Celý rok" : x.label}
          </button>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 text-sm text-muted-foreground font-medium">Od
        <select className={sel} value={p.od} onChange={(e) => p.set(e.target.value, e.target.value > p.do ? e.target.value : p.do)} aria-label="Od měsíce">
          {p.options.map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
        </select>
      </label>
      <label className="inline-flex items-center gap-2 text-sm text-muted-foreground font-medium">Do
        <select className={sel} value={p.do} onChange={(e) => p.set(e.target.value < p.od ? e.target.value : p.od, e.target.value)} aria-label="Do měsíce">
          {p.options.map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
        </select>
      </label>
    </div>
  );
}

function PeriodMobile({ p }) {
  const [open, setOpen] = useState(false);
  const sel = "w-full min-w-0 h-12 rounded-[14px] bg-secondary px-3 text-base font-semibold border-0";
  const btnLabel = p.active ? cap(p.active.key === "rok" ? "letos" : p.active.label) : `${monthShort(p.od)} – ${monthShort(p.do)}`;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="ov-period-btn" aria-label="Změnit období"
        className="md:hidden inline-flex items-center gap-2 h-10 px-3.5 rounded-full bg-card border border-border text-sm font-bold">
        <CalendarDays size={17} /> {btnLabel} <ChevronDown size={15} className="text-muted-foreground" />
      </button>
      <BottomSheet open={open} onOpenChange={setOpen} title="Období" testid="ov-period-sheet">
        <div className="divide-y divide-border">
          {p.presets.map((x) => (
            <button key={x.key} type="button" onClick={() => { p.set(x.od, x.do); setOpen(false); }}
              className="w-full min-h-[52px] flex items-center justify-between text-base font-medium">
              {x.long} {p.active?.key === x.key && <Check size={19} className="text-brand" />}
            </button>
          ))}
          <div className="pt-3">
            <div className="flex items-center justify-between text-base font-medium mb-2">Vlastní {!p.active && <Check size={19} className="text-brand" />}</div>
            <div className="grid grid-cols-2 gap-2.5">
              <label className="text-xs font-semibold text-muted-foreground min-w-0">Od
                <select className={sel} value={p.od} onChange={(e) => p.set(e.target.value, e.target.value > p.do ? e.target.value : p.do)}>
                  {p.options.map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
                </select>
              </label>
              <label className="text-xs font-semibold text-muted-foreground min-w-0">Do
                <select className={sel} value={p.do} onChange={(e) => p.set(e.target.value < p.od ? e.target.value : p.od, e.target.value)}>
                  {p.options.map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
                </select>
              </label>
            </div>
          </div>
        </div>
        <button type="button" onClick={() => setOpen(false)} className="mt-5 w-full h-12 rounded-[16px] bg-primary text-primary-foreground font-bold">Hotovo</button>
      </BottomSheet>
    </>
  );
}

// ---------- společné kousky ----------
function Card({ hero, children, className = "", testid }) {
  return (
    <section data-testid={testid}
      className={`${hero ? "ov-hero" : "bg-card"} border border-border rounded-[20px] p-4 md:p-6 min-w-0 ${className}`}>
      {children}
    </section>
  );
}

function CardHead({ title, onOpen, value, valueClass = "", sub, title2 }) {
  return (
    <div>
      {onOpen ? (
        <button type="button" onClick={onOpen} className="inline-flex items-center gap-1 text-[15px] font-medium text-muted-foreground hover:text-foreground">
          {title} <ChevronRight size={16} />
        </button>
      ) : title && <div className="text-[15px] font-medium text-muted-foreground">{title}</div>}
      {value != null && (
        <div className={`ov-num font-heading font-extrabold tracking-tight tabular-nums whitespace-nowrap text-[28px] md:text-[36px] leading-tight mt-1 ${valueClass}`} title={title2}>{value}</div>
      )}
      {sub && <div className="text-sm text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Table({ head, rows, cols }) {
  return (
    <div className="bg-card border border-border rounded-[20px] overflow-hidden">
      {head && (
        <div className={`hidden md:grid ${cols} gap-4 px-5 py-3 text-xs font-semibold text-muted-foreground border-b border-border`}>
          {head.map((h, i) => <span key={i} className={i ? "text-right" : ""}>{h}</span>)}
        </div>
      )}
      <div className="divide-y divide-border">{rows}</div>
    </div>
  );
}

function Chips({ value, onChange, counts }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Druh nákladů">
      {["Materiál", "Subdodávky", "Práce", "Režie", "Ostatní"].map((n, k) => (
        <button key={n} type="button" onClick={() => onChange(k)} aria-pressed={value === k}
          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[13px] font-semibold transition-colors ${value === k ? "bg-foreground text-background" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
          <i className="w-2 h-2 rounded-[3px]" style={{ background: CAT_COLORS[k] }} />{n}
        </button>
      ))}
    </div>
  );
}

function Back({ onClick, title }) {
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={onClick} aria-label="Zpět na přehled" className="w-10 h-10 rounded-full grid place-items-center hover:bg-secondary">
        <ArrowLeft size={21} />
      </button>
      <h2 className="font-heading font-extrabold text-[22px] md:text-[26px] tracking-tight">{title}</h2>
    </div>
  );
}

// ---------- 1) Příjmy a výdaje ----------
// 289: peníze s DPH podle plateb = příjmy a výdaje; náklady a tržby (bez DPH) má Výsledek.
function cashTip(row) {
  const fu = row.kind !== "past";
  const r = row.in - row.out;
  return (
    <>
      <div className="tt">{cap(monthName(row.m))}{row.kind === "now" ? " · zaplaceno a splatné" : fu ? " · podle splatnosti" : " · zaplaceno"}</div>
      <TipRow color={C.in} value={money(row.in)} label={fu ? "očekávané příjmy" : "příjmy"} />
      <TipRow color={C.out} value={money(row.out)} label={fu ? "očekávané výdaje" : "výdaje"} />
      <TipRow color={r < 0 ? C.red : C.in} value={signed(r)} label="rozdíl" />
    </>
  );
}

function CashChart({ cash, height, compact, bars }) {
  const rows = cash.months;
  const split = rows.findIndex((r) => r.kind === "now");
  if (bars) {
    return (
      <ColumnChart compact={compact} height={height} thinLabels={compact && rows.length > 9}
        columns={rows.map((r) => ({ label: monthShort(r.m), title: monthName(r.m),
          bars: [{ value: r.in, color: C.in, faded: r.kind === "future" }, { value: r.out, color: C.out, faded: r.kind === "future" }],
          tip: cashTip(r) }))} />
    );
  }
  return (
    <LineChart height={height} compact={compact} labels={rows.map((r) => monthShort(r.m))}
      series={[{ values: rows.map((r) => r.in), color: C.in }, { values: rows.map((r) => r.out), color: C.out }]}
      split={split >= 0 ? split : null} zoneLabel="plán: faktury, mzdy, režie" tip={(i) => cashTip(rows[i])} />
  );
}

const cashLegend = [{ label: "Příjmy", color: C.in, line: true }, { label: "Výdaje", color: C.out, line: true },
  { label: "Plán: faktury podle splatnosti, mzdy a režie", color: C.muted, dash: true }];

function cashSub(cash) {
  return `očekávané příjmy do konce ${monthGen(cash.months[cash.months.length - 1].m)} z vydaných faktur · očekávané výdaje ${money(cash.out_ahead)} (faktury, mzdy ${plur(cash.team, "člověk", "lidi", "lidí")}, režie) · s DPH`;
}

function PnDetail({ d, tab, setTab, mobile }) {
  const [bars, setBars] = useState(false);
  const cash = d.cash;
  const res = d.result;
  if (tab === "c") {
    let ci = 0, cc = 0;
    const cum = res.map((r) => { ci += r.rev; cc += r.cost; return { m: r.m, rev: ci, cost: cc }; });
    const tot = cum.length ? cum[cum.length - 1] : { rev: 0, cost: 0 };
    return (
      <>
        <Card hero>
          <CardHead value={signed(tot.rev - tot.cost)} valueClass={tot.rev - tot.cost < 0 ? "ov-red-text" : "ov-in-text"}
            sub={`výsledek ${d.label} · tržby ${money(tot.rev)} · náklady ${money(tot.cost)} · sečteno od začátku období · ${d.basis === "net" ? "bez DPH" : "s DPH"}`} />
          <div className="mt-6">
            <LineChart height={mobile ? 230 : 340} compact={mobile} labels={cum.map((r) => monthShort(r.m))}
              series={[{ values: cum.map((r) => r.rev), color: C.in }, { values: cum.map((r) => r.cost), color: C.out }]}
              tip={(i) => (<><div className="tt">Od začátku období do: {monthName(cum[i].m)}</div>
                <TipRow color={C.in} value={money(cum[i].rev)} label="tržby" /><TipRow color={C.out} value={money(cum[i].cost)} label="náklady" />
                <TipRow color={cum[i].rev - cum[i].cost < 0 ? C.red : C.in} value={signed(cum[i].rev - cum[i].cost)} label="výsledek" /></>)} />
          </div>
          <div className="mt-3"><Legend items={[{ label: "Tržby (fakturováno)", color: C.in, line: true }, { label: "Náklady", color: C.out, line: true }]} /></div>
        </Card>
        <MonthTable rows={res.slice().reverse().map((r) => ({ m: r.m, a: r.rev, b: r.cost }))} la="Tržby" lb="Náklady" />
      </>
    );
  }
  return (
    <>
      <Card hero>
        <div className="flex items-start justify-between gap-3">
          <CardHead value={money(cash.in_ahead)} valueClass="ov-in-text" title2={formatCZK(cash.in_ahead)} sub={cashSub(cash)} />
          <div ref={autoPill} className="inline-flex shrink-0 rounded-full bg-secondary p-1" role="group" aria-label="Typ grafu">
            {[[false, LineIcon, "Čára"], [true, BarChart3, "Sloupce"]].map(([v, I, l]) => (
              <button key={l} type="button" onClick={() => setBars(v)} aria-label={l} aria-pressed={bars === v}
                className={`w-9 h-8 grid place-items-center rounded-full ${bars === v ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}><I size={17} /></button>
            ))}
          </div>
        </div>
        <div className="mt-7"><CashChart cash={cash} height={mobile ? 230 : 340} compact={mobile} bars={bars} /></div>
        <div className="mt-3"><Legend items={bars ? [{ label: "Příjmy", color: C.in }, { label: "Výdaje", color: C.out }, { label: "Světlejší = plán", color: C.muted }] : cashLegend} /></div>
      </Card>
      <Table cols="grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))]" head={["Měsíc", "Příjmy", "Výdaje", "Rozdíl"]}
        rows={cash.months.slice().reverse().map((r) => {
          const diff = r.in - r.out;
          const sub = r.kind === "past" ? "zaplaceno" : r.kind === "now"
            ? `nezaplacené příjmy ${money(r.in_rest)}${r.overdue_in ? ` (po splatnosti ${money(r.overdue_in)})` : ""} · očekávané výdaje ${money(r.out_rest)}`
            : `plán · faktury ${money(r.parts.invoices)} · mzdy ${money(r.parts.wages)} · režie ${money(r.parts.rezie)}`;
          return (
            <div key={r.m} className="px-4 md:px-5 py-3 grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))] gap-x-4 items-center">
              <div className="min-w-0">
                <div className="font-semibold">{cap(monthName(r.m))}</div>
                <div className={`text-xs ${r.kind === "future" ? "italic" : ""} text-muted-foreground`}>{sub}</div>
                <div className="md:hidden text-[13px] text-muted-foreground tabular-nums">příjmy {money(r.in)} · výdaje {money(r.out)}</div>
              </div>
              <span className="hidden md:block text-right tabular-nums">{money(r.in)}</span>
              <span className="hidden md:block text-right tabular-nums">{money(r.out)}</span>
              <span className={`text-right font-bold tabular-nums ${diff < 0 ? "ov-red-text" : "ov-in-text"}`}>{signed(diff)}</span>
            </div>
          );
        })} />
    </>
  );
}

function MonthTable({ rows, la, lb }) {
  return (
    <Table cols="grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))]" head={["Měsíc", la, lb, "Výsledek"]}
      rows={rows.map((r) => {
        const diff = r.a - r.b;
        return (
          <div key={r.m} className="px-4 md:px-5 py-3 grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))] gap-x-4 items-center">
            <div className="min-w-0"><div className="font-semibold">{cap(monthName(r.m))}</div>
              <div className="md:hidden text-[13px] text-muted-foreground tabular-nums">{la.toLowerCase()} {money(r.a)} · {lb.toLowerCase()} {money(r.b)}</div></div>
            <span className="hidden md:block text-right tabular-nums">{money(r.a)}</span>
            <span className="hidden md:block text-right tabular-nums">{money(r.b)}</span>
            <span className={`text-right font-bold tabular-nums ${diff < 0 ? "ov-red-text" : "ov-in-text"}`}>{signed(diff)}</span>
          </div>
        );
      })} />
  );
}

// ---------- 2) Náklady ----------
function ghostColumns(rows, labels, sel, onClick) {
  return rows.map((r, i) => ({
    label: labels[i].short, title: labels[i].long, ghost: sum(r),
    bars: [{ value: r[sel] || 0, color: CAT_COLORS[sel] }],
    onClick: onClick ? () => onClick(i) : undefined, aria: onClick ? `Otevřít ${labels[i].long}` : undefined,
    tip: (<><div className="tt">{cap(labels[i].long)}</div>
      <TipRow color={CAT_COLORS[sel]} value={money(r[sel])} label={["materiál", "subdodávky", "práce", "režie", "ostatní"][sel]} />
      <TipRow color={C.muted} value={money(sum(r))} label="všechny náklady" /></>),
  }));
}

function capSel(rows, sel) {
  const a = sum(rows.map((r) => r[sel] || 0)), t = sum(rows.map(sum));
  return { a, t, p: t ? Math.round((a / t) * 100) : 0 };
}

function CostsTile({ d, sel, setSel, open, mobile }) {
  const rows = d.costs.company;
  const labels = d.costs.months.map((m) => ({ short: monthShort(m), long: monthName(m) }));
  const total = sum(rows.map(sum));
  const c = capSel(rows, sel);
  return (
    <Card testid="ov-costs-tile">
      <CardHead title="Náklady" onOpen={open} value={money(total)} title2={formatCZK(total)}
        sub={`${d.label} · práce ${money(d.labor.cost)} · režie Ø ${money(d.rezie.avg)} / měs.`} />
      <div className="mt-4"><Chips value={sel} onChange={setSel} /></div>
      <div className="mt-4"><ColumnChart compact={mobile} height={mobile ? 190 : 210} thinLabels={mobile && labels.length > 9} columns={ghostColumns(rows, labels, sel)} /></div>
      <div className="mt-3 text-[13px] text-muted-foreground"><b className="text-foreground">{["Materiál", "Subdodávky", "Práce", "Režie", "Ostatní"][sel]}</b> {money(c.a)} z {money(c.t)} · {c.p} % všech nákladů</div>
    </Card>
  );
}

function CostsDetail({ d, tab, setTab, sel, setSel, mobile }) {
  const [mode, setMode] = useState("m");            // m = po měsících, z = po zakázkách
  const [pid, setPid] = useState("");
  const costs = d.costs;
  const names = ["Materiál", "Subdodávky", "Práce", "Režie", "Ostatní"];
  const tabs = [{ value: "druh", label: "Podle druhu" }, { value: "prace", label: "Práce" }, { value: "rezie", label: "Režie" }];
  let body;
  if (tab === "prace") body = <LaborTab d={d} mobile={mobile} />;
  else if (tab === "rezie") body = <OverheadTab d={d} mobile={mobile} />;
  else {
    const proj = costs.projects.find((p) => p.id === pid);
    const months = costs.months.map((m) => ({ short: monthShort(m), long: monthName(m) }));
    let rows, labels, onClick, sub, totalRows;
    if (mode === "z") {
      rows = costs.projects.map((p) => p.months.reduce((acc, r) => acc.map((x, k) => x + r[k]), [0, 0, 0, 0, 0]));
      labels = costs.projects.map((p) => ({ short: p.name, long: p.name }));
      onClick = (i) => { setPid(costs.projects[i].id); setMode("m"); };
      sub = `náklady zakázek ${d.label} · bez režie firmy · klepnutím otevřete zakázku`;
      totalRows = rows;
    } else {
      rows = proj ? proj.months : costs.company;
      labels = months;
      sub = proj ? `${proj.name} · ${d.label}` : `všechny náklady firmy ${d.label} · po měsících`;
      totalRows = rows;
    }
    const c = capSel(totalRows, sel);
    const total = sum(totalRows.map(sum));
    const catTot = names.map((_, k) => sum(totalRows.map((r) => r[k] || 0)));
    body = (
      <>
        <Card hero>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <TabSwitcher value={mode} onChange={setMode} tabs={[{ value: "m", label: "Po měsících" }, { value: "z", label: "Po zakázkách" }]} testid="ov-costs-mode" />
            {mode === "m" && (
              <select value={pid} onChange={(e) => setPid(e.target.value)} aria-label="Zakázka"
                className="h-10 max-w-full min-w-0 rounded-full bg-card border border-border px-3 text-sm font-semibold">
                <option value="">Všechny zakázky</option>
                {costs.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
          </div>
          <CardHead value={money(total)} title2={formatCZK(total)} sub={`${sub} · ${d.basis === "net" ? "bez DPH" : "s DPH"}`} />
          <div className="mt-4"><Chips value={sel} onChange={setSel} /></div>
          <div className="mt-5">
            {rows.length ? <ColumnChart compact={mobile} height={mobile ? 230 : 340} thinLabels={mobile && labels.length > 9} columns={ghostColumns(rows, labels, sel, onClick)} />
              : <p className="text-sm text-muted-foreground py-10 text-center">Za toto období tu nejsou žádné náklady.</p>}
          </div>
          <div className="mt-3 text-[13px] text-muted-foreground"><b className="text-foreground">{names[sel]}</b> {money(c.a)} z {money(c.t)} · {c.p} % {mode === "z" ? "nákladů zakázek" : "všech nákladů"}</div>
        </Card>
        {mode === "z" ? (
          <Table rows={costs.projects.map((p, i) => {
            const r = rows[i], t = sum(r);
            return (
              <button key={p.id} type="button" onClick={() => onClick(i)} className="w-full text-left px-4 md:px-5 py-3 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 items-center hover:bg-secondary/50">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{p.name}</div>
                  <div className="flex gap-[2px] h-2 rounded-full overflow-hidden my-1.5">
                    {r.map((x, k) => x > 0 && <span key={k} style={{ width: `${(x / t) * 100}%`, background: CAT_COLORS[k] }} />)}
                  </div>
                  <div className="text-xs text-muted-foreground">{r.map((x, k) => (x > 0 ? `${names[k].toLowerCase()} ${Math.round((x / t) * 100)} %` : null)).filter(Boolean).join(" · ")}</div>
                </div>
                <span className="font-bold tabular-nums">{money(t)}</span>
              </button>
            );
          })} />
        ) : (
          <Table rows={names.map((n, k) => catTot[k] ? (
            <button key={n} type="button" onClick={() => setSel(k)} className={`w-full text-left px-4 md:px-5 py-3 grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-4 items-center hover:bg-secondary/50 ${sel === k ? "bg-secondary/40" : ""}`}>
              <span className="font-semibold inline-flex items-center gap-2.5"><i className="w-2.5 h-2.5 rounded-[3px]" style={{ background: CAT_COLORS[k] }} />{n}</span>
              <span className="text-muted-foreground tabular-nums">{total ? pct((catTot[k] / total) * 100) : "—"}</span>
              <span className="font-bold tabular-nums min-w-[96px] text-right">{money(catTot[k])}</span>
            </button>
          ) : null).filter(Boolean)} />
        )}
      </>
    );
  }
  return (
    <>
      <TabSwitcher value={tab || "druh"} onChange={setTab} tabs={tabs} testid="ov-costs-tabs" />
      {body}
    </>
  );
}

function LaborTab({ d, mobile }) {
  const l = d.labor;
  const avgRate = l.hours ? l.cost / l.hours : 0;
  const kpi = [["Odpracováno", formatHours(l.hours)], ["Náklad práce", money(l.cost)], ["Průměrná sazba", avgRate ? `${Math.round(avgRate).toLocaleString("cs-CZ")} Kč/h` : "—"],
    ["Tržba na 1 hodinu", l.rev_per_hour != null ? `${Math.round(l.rev_per_hour).toLocaleString("cs-CZ")} Kč` : "—"]];
  const avg = l.months.length ? l.cost / l.months.length : 0;
  return (
    <>
      <Card hero>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-5">
          {kpi.map(([k, v]) => (
            <div key={k} className="rounded-[14px] bg-card/60 border border-border px-3.5 py-3 min-w-0">
              <div className="text-xs font-medium text-muted-foreground">{k}</div>
              <div className="font-heading font-extrabold text-lg tabular-nums whitespace-nowrap mt-0.5">{v}</div>
            </div>
          ))}
        </div>
        <ColumnChart compact={mobile} height={mobile ? 200 : 300} avg={!mobile && avg ? { value: avg, label: `Ø ${money(avg)}` } : null}
          columns={l.months.map((m) => ({ label: monthShort(m.m), title: monthName(m.m), bars: [{ value: m.cost, color: CAT_COLORS[2] }],
            tip: (<><div className="tt">{cap(monthName(m.m))}</div><TipRow color={CAT_COLORS[2]} value={money(m.cost)} label="náklad práce" />
              <TipRow color={C.muted} value={formatHours(m.hours)} label="odpracováno" /></>) }))} />
        <div className="mt-3"><Legend items={[{ label: "Náklad práce (hodiny × sazba z docházky)", color: CAT_COLORS[2] }]} /></div>
      </Card>
      <Table cols="grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))]" head={["Zakázka", "Odpracováno", "Náklad práce", "Tržba na hodinu"]}
        rows={l.projects.map((p) => (
          <div key={p.id} className="px-4 md:px-5 py-3 grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))] gap-x-4 items-center">
            <div className="min-w-0"><div className="font-semibold truncate">{p.name}</div>
              <div className="md:hidden text-[13px] text-muted-foreground">{formatHours(p.hours)} · tržba {p.rev_per_hour != null ? `${Math.round(p.rev_per_hour).toLocaleString("cs-CZ")} Kč/h` : "—"}</div></div>
            <span className="hidden md:block text-right tabular-nums">{formatHours(p.hours)}</span>
            <span className="text-right tabular-nums font-bold md:font-normal">{money(p.cost)}</span>
            <span className="hidden md:block text-right tabular-nums">{p.rev_per_hour != null ? `${Math.round(p.rev_per_hour).toLocaleString("cs-CZ")} Kč/h` : "—"}</span>
          </div>
        ))} />
    </>
  );
}

function OverheadTab({ d, mobile }) {
  const r = d.rezie;
  return (
    <>
      <Card hero>
        <CardHead value={`Ø ${money(r.avg)} / měs.`} sub={`režie firmy ${d.label} · ${d.basis === "net" ? "bez DPH" : "s DPH"}`} />
        <div className="mt-5">
          <ColumnChart compact={mobile} height={mobile ? 190 : 280} avg={!mobile && r.avg ? { value: r.avg, label: `Ø ${money(r.avg)}` } : null}
            columns={d.costs.months.map((m, i) => ({ label: monthShort(m), title: monthName(m), bars: [{ value: r.months[i], color: CAT_COLORS[3] }],
              tip: (<><div className="tt">{cap(monthName(m))}</div><TipRow color={CAT_COLORS[3]} value={money(r.months[i])} label="režie" /></>) }))} />
        </div>
        {r.break_even != null && (
          <div className="mt-5 rounded-[14px] bg-card/60 border border-border p-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="text-sm text-muted-foreground flex-1 min-w-[220px]">
              Zakázky vám v průměru nechají {Math.round(r.margin_share * 100)} % z fakturace. Aby pokryly režii {money(r.avg)} měsíčně, musíte fakturovat aspoň
            </span>
            <span className="font-heading font-extrabold text-xl ov-in-text whitespace-nowrap">{money(r.break_even)} / měs.</span>
          </div>
        )}
      </Card>
      <Table rows={r.cats.map((c) => {
        const t = sum(r.cats.map((x) => x.amount));
        return (
          <div key={c.name} className="px-4 md:px-5 py-3 grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-4 items-center">
            <span className="font-semibold truncate">{c.name}</span>
            <span className="text-muted-foreground tabular-nums">{t ? pct((c.amount / t) * 100) : "—"}</span>
            <span className="font-bold tabular-nums min-w-[96px] text-right">{money(c.amount)}</span>
          </div>
        );
      })} />
    </>
  );
}

// ---------- 3) Zakázky ----------
function ProjectsTile({ d, open, mobile }) {
  const z = d.projects;
  const rows = z.rows.slice(0, mobile ? 6 : 8);
  return (
    <Card testid="ov-projects-tile">
      <CardHead title="Zakázky" onOpen={open} value={money(z.costs)} title2={formatCZK(z.costs)} sub={`náklady zakázek z rozpočtu ${money(z.budget)} · ${d.basis === "net" ? "bez DPH" : "s DPH"}`} />
      <div className="mt-5">
        {rows.length ? (
          <ColumnChart compact={mobile} height={mobile ? 190 : 210} columns={rows.map((r) => {
            const over = r.budget && r.costs > r.budget;
            return {
              label: r.name, title: r.name,
              bars: [{ value: r.budget, color: C.budget }, { value: r.costs, color: over ? C.red : C.out }],
              tip: (<><div className="tt">{r.name}</div><TipRow color={C.budget} value={money(r.budget)} label="rozpočet" />
                <TipRow color={over ? C.red : C.out} value={money(r.costs)} label="náklady" />
                {r.budget > 0 && <div className="tn">{Math.round((r.costs / r.budget) * 100)} % rozpočtu{over ? " · přes rozpočet" : ""}</div>}</>),
            };
          })} />
        ) : <p className="text-sm text-muted-foreground py-8 text-center">Zatím žádná zakázka s rozpočtem nebo náklady.</p>}
      </div>
      <div className="mt-3"><Legend items={[{ label: "Rozpočet", color: C.budget }, { label: "Náklady", color: C.out }, { label: "Přes rozpočet", color: C.red }]} /></div>
      <div className="mt-3">
        <span className={`inline-flex rounded-full px-3 py-1.5 text-xs font-semibold ${z.over_budget ? "bg-destructive/10 ov-red-text" : "bg-success/10 ov-in-text"}`}>
          {z.over_budget ? `${plur(z.over_budget, "zakázka je", "zakázky jsou", "zakázek je")} přes rozpočet` : "Všechny zakázky jsou v rozpočtu"}
        </span>
      </div>
    </Card>
  );
}

function HRow({ name, sub, subClass, right, children }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1.3fr)_minmax(0,2fr)_130px] gap-x-4 gap-y-2 items-center py-3">
      <div className="min-w-0"><div className="font-semibold truncate">{name}</div>{sub && <div className={`text-xs ${subClass || "text-muted-foreground"}`}>{sub}</div>}</div>
      <div className="col-span-2 md:col-span-1 order-3 md:order-none">{children}</div>
      <div className="text-right">{right}</div>
    </div>
  );
}

function ProjectsDetail({ d, tab, setTab, mobile }) {
  const z = d.projects;
  const tabs = [{ value: "rozp", label: "Rozpočet" }, { value: "marze", label: "Marže" }, { value: "zas", label: "Zásobník práce" }];
  let body;
  if (tab === "marze") {
    const rows = z.rows.slice().sort((a, b) => b.margin - a.margin);
    const mx = Math.max(1, ...rows.map((r) => Math.abs(r.margin)));
    body = (
      <Card hero>
        <p className="text-sm text-muted-foreground mb-3">marže zatím = vyfakturováno − náklady · {d.basis === "net" ? "bez DPH" : "s DPH"} · vlevo červeně prodělávají</p>
        <div className="divide-y divide-border">
          {rows.map((r, i) => {
            const w = (Math.abs(r.margin) / mx) * 50;
            return (
              <HRow key={r.id} name={r.name} right={<b className={`tabular-nums ${r.margin < 0 ? "ov-red-text" : "ov-in-text"}`}>{signed(r.margin)}</b>}>
                <div className="ov-trk"><i className="mid" /><i className="fill" style={{ "--i": i, left: `${r.margin < 0 ? 50 - w : 50}%`, width: `${w}%`, background: r.margin < 0 ? C.red : C.in }} /></div>
              </HRow>
            );
          })}
        </div>
      </Card>
    );
  } else if (tab === "zas") {
    const rows = z.rows.filter((r) => r.left > 0);
    const mx = Math.max(1, ...rows.map((r) => r.contract));
    body = (
      <Card hero>
        <CardHead value={z.backlog_months != null ? `Práce na ${z.backlog_months.toLocaleString("cs-CZ")} měs.` : money(z.left)}
          sub={`zbývá vyfakturovat ${money(z.left)} · při průměrné fakturaci za ${d.label} · ${d.basis === "net" ? "bez DPH" : "s DPH"}`} />
        <div className="mt-4"><Legend items={[{ label: "Vyfakturováno", color: C.in }, { label: "Zbývá vyfakturovat (obrys)", color: "color-mix(in srgb, var(--ov-in) 35%, transparent)" }]} /></div>
        <div className="divide-y divide-border mt-2">
          {rows.map((r, i) => (
            <HRow key={r.id} name={r.name} sub={`cena díla ${money(r.contract)}${r.end_date ? ` · konec ${new Date(r.end_date).toLocaleDateString("cs-CZ")}` : ""}`}
              right={<><b className="tabular-nums">{money(r.left)}</b><div className="text-xs text-muted-foreground">zbývá</div></>}>
              <div className="flex gap-[3px] h-3">
                <span className="rounded-[4px]" style={{ width: `${(r.invoiced / mx) * 100}%`, background: C.in }} />
                <span className="rounded-[4px]" style={{ width: `${(r.left / mx) * 100}%`, boxShadow: `inset 0 0 0 1.5px ${C.in}`, opacity: 0.6 }} />
              </div>
            </HRow>
          ))}
        </div>
      </Card>
    );
  } else {
    body = (
      <Card hero>
        <p className="text-sm text-muted-foreground">čerpání rozpočtu nákladů proti hotové práci (procento hotovo ze zakázky)</p>
        <div className="mt-2 mb-1"><Legend items={[{ label: "Hotovo", color: "hsl(var(--foreground) / 0.7)" }, { label: "Čerpáno z rozpočtu", color: C.out }, { label: "Čerpáno víc, než je hotovo", color: C.red }]} /></div>
        <div className="divide-y divide-border">
          {z.rows.map((r, i) => {
            const cer = r.budget ? Math.round((r.costs / r.budget) * 100) : null;
            const bad = cer != null && cer - r.progress > 10;
            return (
              <HRow key={r.id} name={r.name}
                sub={`${money(r.costs)} z ${money(r.budget)}${bad ? ` · náklady předbíhají práci o ${cer - r.progress} bodů` : r.budget ? " · v pořádku" : " · bez rozpočtu"}`}
                subClass={bad ? "ov-red-text" : undefined}
                right={<div className="text-xs leading-relaxed"><div>hotovo <b>{r.progress} %</b></div><div>čerpáno <b>{cer != null ? `${cer} %` : "—"}</b></div></div>}>
                <div className="space-y-1.5">
                  <div className="ov-trk h-2"><i className="fill" style={{ "--i": i, left: 0, width: `${Math.min(r.progress, 130) / 1.3}%`, background: "hsl(var(--foreground) / 0.7)" }} /></div>
                  <div className="ov-trk h-2"><i className="fill" style={{ "--i": i, left: 0, width: `${Math.min(cer || 0, 130) / 1.3}%`, background: bad ? C.red : C.out }} /></div>
                </div>
              </HRow>
            );
          })}
        </div>
      </Card>
    );
  }
  return (<><TabSwitcher value={tab || "rozp"} onChange={setTab} tabs={tabs} testid="ov-projects-tabs" />{body}</>);
}

// ---------- stránka ----------
export default function EcoOverview({ actions }) {
  const p = usePeriod();
  const v = useView();
  const mobile = useIsMobile();
  const [sel, setSel] = useState(0);
  const q = useQuery({
    queryKey: ["eco-overview", p.od, p.do],
    queryFn: () => api.get("/economics/overview", { params: { od: p.od, do: p.do } }).then((r) => r.data),
    placeholderData: keepPreviousData, staleTime: 60_000,
  });
  const d = useMemo(() => (q.data ? { ...q.data, label: p.label } : null), [q.data, p.label]);

  const header = (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <PeriodMobile p={p} />
      <div className="hidden md:block" />
      <div className="flex items-center gap-2 ml-auto"><PeriodDesktop p={p} />{typeof actions === "function" ? actions(p) : actions}</div>
    </div>
  );
  if (q.isError) return <div className="eco-ov space-y-4">{header}<p className="text-sm text-destructive" role="alert">Přehled se nepodařilo načíst. Zkuste to prosím znovu.</p></div>;
  if (!d) return <div className="eco-ov space-y-4">{header}<p className="text-sm text-muted-foreground py-10">Načítám…</p></div>;

  const H = mobile ? 200 : 300;
  return (
    <div className={`eco-ov space-y-4 ${q.isFetching ? "opacity-80 transition-opacity" : ""}`} data-testid="eco-overview">
      {header}
      {v.view === "home" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card hero className="lg:col-span-2" testid="ov-cash-tile">
            <CardHead title="Příjmy a výdaje" onOpen={() => v.go("pn")} value={money(d.cash.in_ahead)} valueClass="ov-in-text"
              title2={formatCZK(d.cash.in_ahead)} sub={cashSub(d.cash)} />
            <div className="mt-7"><CashChart cash={d.cash} height={H} compact={mobile} /></div>
            <div className="mt-3"><Legend items={cashLegend} /></div>
          </Card>
          <CostsTile d={d} sel={sel} setSel={setSel} open={() => v.go("naklady", "druh")} mobile={mobile} />
          <ProjectsTile d={d} open={() => v.go("zakazky", "rozp")} mobile={mobile} />
        </div>
      )}
      {v.view === "pn" && (
        <div className="space-y-4">
          <Back onClick={() => v.go("home")} title="Příjmy a výdaje" />
          <TabSwitcher value={v.tab || "m"} onChange={(t) => v.go("pn", t)} tabs={[{ value: "m", label: "Po měsících" }, { value: "c", label: "Od začátku období" }]} testid="ov-pn-tabs" />
          <PnDetail d={d} tab={v.tab || "m"} mobile={mobile} />
        </div>
      )}
      {v.view === "naklady" && (
        <div className="space-y-4">
          <Back onClick={() => v.go("home")} title="Náklady" />
          <CostsDetail d={d} tab={v.tab} setTab={(t) => v.go("naklady", t)} sel={sel} setSel={setSel} mobile={mobile} />
        </div>
      )}
      {v.view === "zakazky" && (
        <div className="space-y-4">
          <Back onClick={() => v.go("home")} title="Zakázky" />
          <ProjectsDetail d={d} tab={v.tab} setTab={(t) => v.go("zakazky", t)} mobile={mobile} />
        </div>
      )}
    </div>
  );
}
